import dynamic from 'next/dynamic'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Icon } from '@/components/Icon/Icon'
import { IconButton } from '@/components/IconButton/IconButton'
import { publicStoriesDetail } from '@/shared/api/generated/public-stories/public-stories'
import { pxToRem } from '@/shared/styles/themes'
import { toBookViewerStory } from '@/slices/Preview/components/BookViewer/toBookViewerStory'
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons/faArrowLeft'
import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight'
import { Box, Collapse, Skeleton, styled, Typography } from '@mui/material'

import type { StoryDetail } from '@/shared/api/models'

const BookViewer = dynamic(
  () => import('@/slices/Preview/components/BookViewer/BookViewer').then((mod) => mod.BookViewer),
  { ssr: false },
)

interface SampleStoryPreviewProps {
  slugs: readonly string[]
  expanded: boolean
}

export const SampleStoryPreview: React.FC<SampleStoryPreviewProps> = ({
  slugs,
  expanded,
}) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [stories, setStories] = useState<Map<number, StoryDetail>>(new Map())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const fetchedRef = useRef<Set<number>>(new Set())

  const fetchStory = useCallback(async (index: number) => {
    if (fetchedRef.current.has(index) || !slugs[index]) return

    fetchedRef.current.add(index)
    setLoading(true)
    setError(false)

    try {
      const data = await publicStoriesDetail(slugs[index])

      if (data.story) {
        const viewerStory = toBookViewerStory(data.story)

        setStories((prev) => new Map(prev).set(index, viewerStory))
      }
    } catch {
      setError(true)
      fetchedRef.current.delete(index)
    } finally {
      setLoading(false)
    }
  }, [slugs])

  useEffect(() => {
    if (expanded) {
      fetchStory(activeIndex)
    }
  }, [expanded, activeIndex, fetchStory])

  const hasMultiple = slugs.length > 1
  const currentStory = stories.get(activeIndex)

  const goNext = () => {
    setActiveIndex((i) => Math.min(i + 1, slugs.length - 1))
  }

  const goPrev = () => {
    setActiveIndex((i) => Math.max(i - 1, 0))
  }

  return (
    <Collapse in={expanded} timeout={400} unmountOnExit>
      <StyledPreviewSection>
        <StyledHeader>
          {hasMultiple && (
            <IconButton onClick={goPrev} disabled={activeIndex === 0} size="small">
              <Icon icon={faArrowLeft} />
            </IconButton>
          )}

          <Typography variant="h5" fontWeight={600}>
            Sample Story Preview{hasMultiple ? ` ${activeIndex + 1}/${slugs.length}` : ''}
          </Typography>

          {hasMultiple && (
            <IconButton onClick={goNext} disabled={activeIndex === slugs.length - 1} size="small">
              <Icon icon={faArrowRight} />
            </IconButton>
          )}
        </StyledHeader>

        <StyledBookWrapper>
          {loading && !currentStory && (
            <Skeleton variant="rounded" width="100%" height={500} />
          )}

          {error && !currentStory && (
            <StyledErrorBox>
              <Typography color="text.secondary">
                Unable to load preview. Please try again later.
              </Typography>
            </StyledErrorBox>
          )}

          {currentStory && (
            <BookViewer
              story={currentStory}
              isPreview={false}
              isActive={expanded}
            />
          )}
        </StyledBookWrapper>
      </StyledPreviewSection>
    </Collapse>
  )
}

const StyledPreviewSection = styled(Box)(({ theme }) => ({
  padding: `${pxToRem(32)} 0 ${pxToRem(48)}`,
  borderTop: `1px solid ${theme.palette.divider}`,
}))

const StyledHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: pxToRem(16),
  marginBottom: pxToRem(24),
})

const StyledBookWrapper = styled(Box)({
  width: '100%',
  maxWidth: pxToRem(900),
  margin: '0 auto',
  minHeight: pxToRem(400),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const StyledErrorBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: pxToRem(300),
})
